import { devtools, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { createStore } from "zustand/vanilla";
import type {
	AuctionStatus,
	HeaderBiddingStoreState,
	HydraAuction,
	VendorAuctionState,
	VendorName,
} from "@/ts/storeTypes/headerBiddingTypes";
import type { AuctionData, SlotTargeting } from "@/ts/types";

const initialVendorAuctionState: VendorAuctionState = {
	status: "notStarted",
};

const initialState = {
	auctions: {},
	vendorNames: [],
	auctionData: {
		ortb2: {},
		pageMisc: {
			channel: "",
			pageType: "",
			sensitiveArticle: false,
		},
	},
};

export const headerBiddingStore = createStore<HeaderBiddingStoreState>()(
	devtools(
		subscribeWithSelector(
			immer((set, get) => ({
				...initialState,
				actions: {
					addVendor: (vendor: VendorName) =>
						set((state) => {
							state.vendorNames = [...state.vendorNames, vendor];
						}),
					updateVendorAuctionStatus: (
						auctionId: number,
						vendorName: VendorName,
						status: AuctionStatus,
					) =>
						set((state) => {
							state.auctions[auctionId].vendors[vendorName].status = status;
						}),
					updateSlotTargeting: (
						auctionId: number,
						slotId: string,
						targeting: SlotTargeting,
					) =>
						set((state) => {
							const auction = state.auctions[auctionId];
							if (auction) {
								auction.targeting[slotId] = {
									...auction.targeting[slotId],
									...targeting,
								};
							}
						}),
					addAuctionToStore: (auctionId: number, vendorNames: VendorName[]) =>
						set((state) => {
							const vendorsInitialState = vendorNames.map(
								(vendor) => [vendor, initialVendorAuctionState] as const,
							);
							const initialAuction: HydraAuction = {
								done: false,
								vendors: Object.fromEntries(vendorsInitialState),
								targeting: {},
							};
							state.auctions[auctionId] = initialAuction;
						}),
					setAuctionDone: (auctionId: number) =>
						set((state) => {
							state.auctions[auctionId].done = true;
						}),
					addAuctionData: (auctionData: AuctionData) =>
						set((state) => {
							state.auctionData = auctionData;
						}),
				},
				selectors: {
					allVendorAuctionsDone:
						(auctionId: number) =>
						(state: HeaderBiddingStoreState): boolean => {
							const { auctions } = state;
							const vendors = auctions[auctionId].vendors;
							return Object.values(vendors).every(
								(vendor) => vendor.status === "done",
							);
						},
					getSlotTargeting: (
						auctionId: number,
					): Record<string, SlotTargeting> | undefined => {
						const { auctions } = get();
						return auctions[auctionId].targeting;
					},
					getAuctionData: () => get().auctionData,
				},
			})),
		),
		{
			name: "Hydra-HeaderBiddingStore",
		},
	),
);
